import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  input,
  model,
  signal,
  effect,
  DOCUMENT,
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import {
  isVerticalScrollContainer,
  resolveWindowScrollActiveSectionId,
} from './scroll-spy-active-section.util';

@Directive({
  selector: '[appScrollSpy]',
  exportAs: 'appScrollSpy',
})
export class ScrollSpyDirective implements OnDestroy {
  readonly ids = input<string[]>([]);
  readonly offset = input<number>();
  readonly activeSection = model<string | undefined>();
  readonly scrollContainer = input<HTMLElement | undefined>();

  private readonly disableScrollListener = signal<
    | {
        id: string;
        disabled: boolean;
      }
    | undefined
  >(undefined);

  private scrollListener?: () => void;
  private customScrollContainer?: HTMLElement;

  private getEffectiveScrollContainer(): HTMLElement {
    return this.scrollContainer() ?? this.elementRef.nativeElement;
  }

  /**
   * Shop catalog scrolls on the window. Only treat the host as the scroller when it
   * actually has overflow-y scroll/auto — `scrollHeight > clientHeight` alone is a
   * false positive on mobile (subpixels) and then window:scroll is ignored.
   */
  private usesWindowScroll(): boolean {
    if (this.scrollContainer()) {
      return false;
    }
    return !isVerticalScrollContainer(this.elementRef.nativeElement);
  }

  constructor(
    @Inject(DOCUMENT)
    private document: Document,
    private settingsSelector: SettingsStoreSelector,
    private readonly viewportScroller: ViewportScroller,
    private readonly elementRef: ElementRef,
  ) {
    effect(() => {
      this.ids();
      this.offset();
      setTimeout(() => this.checkPosition());
    });

    effect(() => {
      if (this.usesWindowScroll()) {
        if (this.customScrollContainer && this.scrollListener) {
          this.customScrollContainer.removeEventListener('scroll', this.scrollListener);
          this.customScrollContainer.removeEventListener('touchmove', this.scrollListener);
          this.customScrollContainer = undefined;
          this.scrollListener = undefined;
        }
        return;
      }

      const container = this.getEffectiveScrollContainer();
      if (
        container &&
        container !== this.document.body &&
        container !== this.document.documentElement
      ) {
        this.setupCustomScrollListener(container);
      }
    });
  }

  private setupCustomScrollListener(container: HTMLElement | undefined): void {
    if (this.customScrollContainer && this.scrollListener) {
      this.customScrollContainer.removeEventListener('scroll', this.scrollListener);
      this.customScrollContainer.removeEventListener('touchmove', this.scrollListener);
    }

    this.customScrollContainer = container;

    if (container) {
      let ticking = false;
      this.scrollListener = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            if (!this.disableScrollListener()) {
              this.checkPosition();
            }
            ticking = false;
          });
          ticking = true;
        }
      };

      container.addEventListener('scroll', this.scrollListener, { passive: true });
      container.addEventListener('touchmove', this.scrollListener, { passive: true });
    }
  }

  public async scrollToSectionId(id: string | undefined) {
    if (!id) {
      return;
    }
    this.disableScrollListener.set({
      id,
      disabled: true,
    });

    const container = this.getEffectiveScrollContainer();
    const useCustomScroll =
      !this.usesWindowScroll() &&
      container &&
      container !== this.document.body &&
      container !== this.document.documentElement;

    if (useCustomScroll) {
      const element = this.document.getElementById(id);
      if (element) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTopRelativeToContainer =
          elementRect.top - containerRect.top + container.scrollTop;
        const offsetValue = this.offset() ?? 0;
        container.scrollTo({
          top: elementTopRelativeToContainer - offsetValue,
          behavior: 'smooth',
        });
      }
    } else {
      if (this.elementRef?.nativeElement) {
        const topOffset = (this.offset() ?? this.elementRef.nativeElement.offsetHeight) || 30;
        this.viewportScroller.setOffset([0, topOffset]);
      }
      this.viewportScroller.scrollToAnchor(id);
    }

    setTimeout(() => {
      if (this.disableScrollListener()?.id === id) {
        this.disableScrollListener.set(undefined);
      }
    }, 1500);
  }

  private currentSection!: string;

  @HostListener('window:scroll')
  @HostListener('document:scroll')
  onScroll(): void {
    if (!this.usesWindowScroll()) {
      const container = this.getEffectiveScrollContainer();
      if (
        container &&
        container !== this.document.body &&
        container !== this.document.documentElement
      ) {
        return;
      }
    }
    if (this.disableScrollListener()) {
      return;
    }
    this.checkPosition();
  }

  checkPosition(): void {
    const ids = this.ids();
    if (!ids?.length) {
      return;
    }
    let currentSection: string | undefined;
    const container = this.getEffectiveScrollContainer();
    const isDesktop = this.settingsSelector.isDesktopScreen;
    const offsetValue = this.offset() ?? (isDesktop ? 70 : 55);

    const elements = ids
      .map(tag => this.document.getElementById(tag) as HTMLElement)
      .filter(el => !!el);

    const useCustomScroll =
      !this.usesWindowScroll() &&
      container &&
      container !== this.document.body &&
      container !== this.document.documentElement;

    if (useCustomScroll && container) {
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const isAtBottom = scrollTop + containerHeight >= container.scrollHeight - 10;

      currentSection = resolveWindowScrollActiveSectionId({
        sections: elements.map(el => {
          const elementRect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          return {
            id: el.id,
            top: elementRect.top - containerRect.top,
          };
        }),
        offset: offsetValue,
        isAtBottom,
        isAncestor: (ancestorId, descendantId) =>
          this.sectionContains(elements, ancestorId, descendantId),
      });
    } else {
      const documentEl = this.document.documentElement;
      const isAtBottom =
        window.innerHeight +
          (window.scrollY || documentEl.scrollTop || this.document.body.scrollTop || 0) >=
        (documentEl.scrollHeight || this.document.body.scrollHeight) - 10;

      currentSection = resolveWindowScrollActiveSectionId({
        sections: elements.map(el => ({
          id: el.id,
          top: el.getBoundingClientRect().top,
        })),
        offset: offsetValue,
        isAtBottom,
        isAncestor: (ancestorId, descendantId) =>
          this.sectionContains(elements, ancestorId, descendantId),
      });
    }

    if (this.shouldKeepDescendantSection(currentSection, elements)) {
      return;
    }

    if (currentSection && currentSection !== this.currentSection) {
      this.currentSection = currentSection;
      this.activeSection.set(this.currentSection);
    }
  }

  /** A parent box contains its subcategory sections; the scrolled leaf must win. */
  private sectionContains(
    elements: HTMLElement[],
    ancestorId: string,
    descendantId: string,
  ): boolean {
    const ancestor = elements.find(element => element.id === ancestorId);
    const descendant = elements.find(element => element.id === descendantId);
    return !!ancestor && !!descendant && ancestor.contains(descendant);
  }

  /**
   * Growing the sticky tab stack moves the offset down and would otherwise
   * jump back to the parent category, which collapses those tabs again.
   */
  private shouldKeepDescendantSection(
    nextSection: string | undefined,
    elements: HTMLElement[],
  ): boolean {
    if (!nextSection || !this.currentSection || nextSection === this.currentSection) {
      return false;
    }

    const currentElement = elements.find(element => element.id === this.currentSection);
    const nextElement = elements.find(element => element.id === nextSection);
    if (!currentElement || !nextElement || !nextElement.contains(currentElement)) {
      return false;
    }

    return currentElement.getBoundingClientRect().top <= window.innerHeight * 0.5;
  }

  ngOnDestroy(): void {
    if (this.customScrollContainer && this.scrollListener) {
      this.customScrollContainer.removeEventListener('scroll', this.scrollListener);
      this.customScrollContainer.removeEventListener('touchmove', this.scrollListener);
    }
  }
}
